<template>
  <div>
    <div
      v-if="loadingQuote"
      class="loader__wrapper"
    >
      <SkLoader size="x-large" />
    </div>

    <template v-else>
      <router-view @redirect-to-skello="redirectUserToSkello" />
    </template>
  </div>
</template>

<script>
import 'dayjs/locale/fr';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/es';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
import {
  mapState,
  mapActions,
  mapGetters,
  mapMutations,
} from 'vuex';
import { SkLoader } from '@skelloapp/skello-ui';
import { loadLanguage } from '@/plugins/i18n';
import { VUE_APP_SKELLO_APP_ORIGIN } from '@/shared/config';
import { skelloHttpClient } from '@/shared/utils/clients/skelloHttpClient';
import { authClient } from '@/shared/utils/clients/authClient';

import {
  FETCH_FAILED,
  PROVISIONING_FAILED,
  INVALID_QUOTE,
} from '@/shared/constants/errors';

export default {
  name: 'App',
  components: {
    SkLoader,
  },
  data() {
    return {
      loadingQuote: true,
    };
  },
  computed: {
    ...mapState(['quote', 'password']),
    ...mapGetters(['isContractChange', 'isDevFeatureFlagActive', 'isContractChangeFromSalesforce', 'isContractChangeFromInAppAndMassMarketCustomer', 'isCatalogVersionV2']),

    areShopsCreated() {
      return this.quote.organisation.shops.every(shop => shop.SK_shop_id);
    },
    isOnboardingFinished() {
      return this.quote.organisation.SK_organisation_id &&
        this.quote.organisation.contacts[0].SK_user_id && this.areShopsCreated;
    },
    isQuoteStatusValid() {
      return ['Sent', 'Presented', 'Accepted'].includes(this.quote.status);
    },
    isQuoteInvalid() {
      return !this.quote ||
        !this.quote.organisation ||
        this.isQuoteExpired ||
        !this.isQuoteStatusValid;
    },
    isQuoteExpired() {
      const expirationDate = new Date(this.quote.valid_till);
      if (!expirationDate) return false;

      // Add 1 day to make the quote valid for the entire expiration day
      const nextDay = new Date(expirationDate);
      nextDay.setDate(expirationDate.getDate() + 1);

      // JS counts in milliseconds, other programs in seconds
      // In order to avoid float bugs we convert up, not down
      return new Date() > nextDay;
    },
    quoteId() {
      return this.$route.query.id;
    },
  },
  async mounted() {
    if (['error', 'contractChangeError', 'successPage'].includes(this.$route.name)) {
      this.loadingQuote = false;
      return;
    }

    if (!this.quoteId) {
      this.redirectToErrorPage(FETCH_FAILED, 'error');
      return;
    }

    await this.fetchFeatureFlags();

    const onboardingLastStepPath = '/onboarding/create_password';

    this.fetchQuote(this.quoteId)
      .then(() => {
        if (!this.isQuoteInvalid && this.isOnboardingFinished && !this.isContractChange) {
          this.redirectUserToSkello();
        }

        if (this.isContractChange) {
          // If the user refreshes after submitting the last step of onboarding, the quote is already considered a contract change,
          // but we don't want to redirect them to the contract change page
          if (this.$router.currentRoute.path === onboardingLastStepPath) {
            return;
          }
          // Rollout in app
          if (!this.isQuoteStatusValid) {
            this.redirectToErrorPage(INVALID_QUOTE, 'contractChangeError');
            return;
          }
          // Contract change from SF
          if (this.isContractChangeFromSalesforce && this.isQuoteInvalid) {
            this.redirectToErrorPage(INVALID_QUOTE, 'contractChangeError');
            return;
          }

          try {
            this.populatePaymentInfos();
            if (this.isContractChangeFromInAppAndMassMarketCustomer && this.isCatalogVersionV2) {
              this.populateLineItemsInfos();
            }
          } catch (error) {
            if (error.message === 'invalid quote') {
              console.error(error);
              this.makeAlertToast(this.$t('common.error_message'));
              this.redirectToErrorPage(INVALID_QUOTE, 'contractChangeError');
            } else {
              throw error;
            }

            return;
          }
        }

        const resolveName = this.isContractChange ? 'contractChange' : 'onboarding';

        if (this.$route.name !== resolveName) {
          this.$router.push({ name: resolveName, query: this.$route.query });
        }
      })
      .catch(error => {
        this.makeAlertToast(this.$t('common.error_message'));
        this.redirectToErrorPage(FETCH_FAILED, 'error');
        throw error;
      })
      .finally(async () => {
        // Turn the SF quote locale (EN_uk) into i18n locale (en)
        const lang =
          this.isContractChange && this.$route.query.locale ?
            this.$route.query.locale :
            this.quote.locale?.slice(0, 2)?.toLowerCase();

        await loadLanguage(lang || 'fr');
        this.loadingQuote = false;
      });
  },
  methods: {
    ...mapActions([
      'fetchQuote',
      'fetchFeatureFlags',
    ]),
    ...mapMutations(['populatePaymentInfos', 'populateLineItemsInfos']),

    redirectToErrorPage(errorType, errorNameComponent) {
      if (this.loadingQuote) this.loadingQuote = false;
      this.$router.push(
        { name: errorNameComponent, query: this.$route.query, params: { error: errorType } },
      );
    },
    async redirectUserToSkello() {
      if (['contractChangeError', 'error'].includes(this.$route.name)) return;

      const baseUrl = VUE_APP_SKELLO_APP_ORIGIN;
      let token = this.$route.query.t;

      const hasCredential = (this.quote.organisation.contacts[0]?.email && this.password) || token;

      if (this.isContractChangeFromSalesforce || !hasCredential) {
        window.location.href = baseUrl;

        return;
      }

      try {
        if (!token && this.password) {
          const data = await authClient.login({
            email: this.quote.organisation.contacts[0].email,
            password: this.password,
          });

          token = data.token;
        }

        const response = await skelloHttpClient.get('/v3/users/sessions/stateless_token/deal_room', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        window.location.href = `${baseUrl}?deal_room=${response.data.token}&purpose=deal_room`;
      } catch (error) {
        this.makeAlertToast(this.$t('common.error_message'));
        this.redirectToErrorPage(PROVISIONING_FAILED, 'error');
      }
    },
  },
};
</script>

<style lang="scss">
// Application-wide css
* {
  box-sizing: border-box;

  // Recenters the tick in the checkbox
  &::before,
  &::after {
    box-sizing: inherit;
  }
}
.salesforce-env {
  position: absolute;
  top: 0;
  left: 0;
  background-color: #fff;
  padding: 20px;
  border-radius: 10px;
  fieldset {
    border: 1px solid #ccc;
    border-radius: 5px;
    padding: 10px;
    legend {
      font-size: 1.2em;
      font-weight: bold;
    }
    div {
      display: flex;
      align-items: center;
      input {
        margin-right: 5px;
      }
    }
  }
}
.loader__wrapper{
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
}
</style>
